import { useMemo } from 'react';
import type { HypothesisCreativeEntry, HypothesisGroup, } from '../types/hypothesis';
import { orderByIds } from '../utils/orderByIds';
import { NO_HYPOTHESIS_KEY } from 'features/facebook';

export type UnpackedOrderByHypothesis = Record<string, string[]>;

interface UseOrderedHypothesisGroupsArgs {
  thisWeek: HypothesisGroup[];
  upcoming: HypothesisGroup[];
  noHypothesis: HypothesisCreativeEntry[];
  unpackedOrderByHypothesis: UnpackedOrderByHypothesis;
}

interface UseOrderedHypothesisGroupsResult {
  orderedThisWeek: HypothesisGroup[];
  orderedUpcoming: HypothesisGroup[];
  orderedNoHypothesis: HypothesisCreativeEntry[];
}

const orderCreativesForHypothesis = (
  hypothesisKey: string,
  creatives: HypothesisCreativeEntry[],
  unpackedOrderByHypothesis: UnpackedOrderByHypothesis,
): HypothesisCreativeEntry[] =>
  orderByIds(creatives, unpackedOrderByHypothesis[hypothesisKey]);

const useOrderedHypothesisGroups = ({
  thisWeek,
  upcoming,
  noHypothesis,
  unpackedOrderByHypothesis,
}: UseOrderedHypothesisGroupsArgs): UseOrderedHypothesisGroupsResult =>
  useMemo(() => {
    const orderGroup = (group: HypothesisGroup): HypothesisGroup => ({
      ...group,
      creatives: orderCreativesForHypothesis(
        group.hypothesis,
        group.creatives,
        unpackedOrderByHypothesis,
      ),
    });

    return {
      orderedThisWeek: thisWeek.map(orderGroup),
      orderedUpcoming: upcoming.map(orderGroup),
      orderedNoHypothesis: orderCreativesForHypothesis(
        NO_HYPOTHESIS_KEY,
        noHypothesis,
        unpackedOrderByHypothesis,
      ),
    };
  }, [thisWeek, upcoming, noHypothesis, unpackedOrderByHypothesis]);

export default useOrderedHypothesisGroups;
